import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  Calendar,
  Clock,
  User,
  Briefcase,
  Video,
  Trash2,
  ChevronLeft,
  ChevronRight,
  X,
  AlertTriangle
} from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './components/ui/Table';
import { scheduleInterviewApi } from './utils/api';
import { useNotification } from './CustomNotificationSystem';

const InterviewScheduleManager = ({ user, company }) => {
  const navigate = useNavigate();
  const notify = useNotification();
  
  // State management
  const [interviews, setInterviews] = useState([]);
  const [videoRooms, setVideoRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false
  });

  // Filter state
  const [filters, setFilters] = useState({
    candidateName: '',
    jobTitle: '',
    dateFrom: '',
    dateTo: ''
  });

  // UI state
  // Note: Sorting state removed since we use backend sorting
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, interview: null });
  const [activeTab, setActiveTab] = useState('scheduled'); // 'scheduled' or 'video-rooms'

  // Fetch interviews
  const fetchInterviews = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        limit: pagination.limit,
        offset: pagination.offset
      };

      const response = await scheduleInterviewApi.fetchAllInterviews(params);
      
      if (response.success) {
        console.log('Raw interview data from API:', response.interviews);
        console.log('Current date for comparison:', new Date());
        console.log('Interview dates breakdown:');
        response.interviews.forEach((interview, index) => {
          console.log(`Interview ${index + 1}:`, {
            id: interview.id,
            date: interview.date,
            time: interview.time,
            scheduledDateTime: interview.scheduledDateTime,
            candidateName: interview.candidateName
          });
        });
        setInterviews(response.interviews);
        setPagination(response.pagination);
      } else {
        notify('Failed to fetch interviews', 'error');
      }
    } catch (error) {
      console.error('Error fetching interviews:', error);
      notify('Error loading interviews', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit, pagination.offset, notify]);

  // Fetch video rooms
  const fetchVideoRooms = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        limit: pagination.limit,
        offset: pagination.offset
      };

      const response = await scheduleInterviewApi.fetchAllVideoRooms(params);
      
      if (response.success) {
        console.log('Raw video room data from API:', response.interviews);
        setVideoRooms(response.interviews || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          hasMore: response.pagination?.hasMore || false
        }));
      } else {
        notify('Failed to fetch video rooms', 'error');
      }
    } catch (error) {
      console.error('Error fetching video rooms:', error);
      notify('Error loading video rooms', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit, pagination.offset, notify]);

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'scheduled') {
      fetchInterviews();
    } else if (activeTab === 'video-rooms') {
      fetchVideoRooms();
    }
  }, [activeTab, fetchInterviews, fetchVideoRooms]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPagination(prev => ({ ...prev, offset: 0 })); // Reset to first page
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      candidateName: '',
      jobTitle: '',
      dateFrom: '',
      dateTo: ''
    });
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  // Note: Sorting is now handled by the backend (upcoming first, then past)

  // Get current data based on active tab
  const getCurrentData = () => {
    return activeTab === 'scheduled' ? interviews : videoRooms;
  };

  // Use backend sorting (upcoming first, then past) - no frontend sorting needed
  const sortedData = getCurrentData();

  // Handle pagination
  const handlePageChange = (newOffset) => {
    setPagination(prev => ({ ...prev, offset: newOffset }));
  };

  // Handle join video call
  const handleJoinCall = (item) => {
    const joinUrl = item.interviewerJoinUrl;
    
    if (joinUrl) {
      // Open the join URL in a new tab
      window.open(joinUrl, '_blank');
    } else {
      notify('No video room join link available', 'warning');
    }
  };

  // Handle view job
  const handleViewJob = (interview) => {
    if (interview.jobId) {
      navigate(`/job/edit/${interview.jobId}`);
    } else {
      notify('No job information available', 'warning');
    }
  };

  // Handle delete interview or video room
  const handleDeleteInterview = async () => {
    if (!deleteModal.interview) return;

    try {
      let response;
      const isVideoRoom = activeTab === 'video-rooms';
      
      if (isVideoRoom) {
        response = await scheduleInterviewApi.deleteVideoRoom(deleteModal.interview.id);
      } else {
        response = await scheduleInterviewApi.deleteScheduleInterview(deleteModal.interview.id);
      }
      
      if (response.success) {
        notify(`${isVideoRoom ? 'Video room' : 'Interview'} deleted successfully`, 'success');
        setDeleteModal({ isOpen: false, interview: null });
        
        // Refresh the appropriate list
        if (isVideoRoom) {
          fetchVideoRooms();
        } else {
          fetchInterviews();
        }
      } else {
        notify(`Failed to delete ${isVideoRoom ? 'video room' : 'interview'}`, 'error');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      notify(`Error deleting ${activeTab === 'video-rooms' ? 'video room' : 'interview'}`, 'error');
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    
    try {
      let day, month, year;
      
      if (dateStr.includes('-')) {
        // DD-MM-YYYY format
        [day, month, year] = dateStr.split('-');
      } else if (dateStr.includes('/')) {
        // DD/MM/YYYY format
        [day, month, year] = dateStr.split('/');
      } else {
        console.error('Unknown date format for display:', dateStr);
        return dateStr;
      }
      
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateStr;
    }
  };

  // Format time for display
  const formatTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    return timeStr;
  };

  // Get time until interview
  const getTimeUntilInterview = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;
    
    try {
      console.log('Frontend parsing date/time:', { dateStr, timeStr });
      
      // Handle different date formats
      let dateStrFormatted;
      if (dateStr.includes('-')) {
        // DD-MM-YYYY format
        const [day, month, year] = dateStr.split('-');
        dateStrFormatted = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      } else if (dateStr.includes('/')) {
        // DD/MM/YYYY format
        const [day, month, year] = dateStr.split('/');
        dateStrFormatted = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      } else {
        console.error('Unknown date format:', dateStr);
        return null;
      }
      
      // Ensure time has seconds
      const timeStrFormatted = timeStr.includes(':') ? 
        (timeStr.split(':').length === 2 ? `${timeStr}:00` : timeStr) : 
        `${timeStr}:00:00`;
      
      const interviewDateTime = new Date(`${dateStrFormatted}T${timeStrFormatted}`);
      const now = new Date();
      
      console.log('Parsed datetime:', {
        originalDate: dateStr,
        originalTime: timeStr,
        formattedDate: dateStrFormatted,
        formattedTime: timeStrFormatted,
        interviewDateTime,
        now,
        isValid: !isNaN(interviewDateTime.getTime())
      });
      
      if (isNaN(interviewDateTime.getTime())) {
        console.error('Invalid date created:', interviewDateTime);
        return null;
      }
      
      const diffMs = interviewDateTime - now;
      
      if (diffMs < 0) {
        return { text: 'Past', type: 'past' };
      }
      
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffDays > 0) {
        return { text: `${diffDays}d`, type: diffDays <= 1 ? 'urgent' : 'upcoming' };
      } else if (diffHours > 0) {
        return { text: `${diffHours}h`, type: diffHours <= 2 ? 'urgent' : 'upcoming' };
      } else {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        return { text: `${diffMinutes}m`, type: 'urgent' };
      }
    } catch (error) {
      console.error('Error calculating time until interview:', error);
      return null;
    }
  };

  // Get time until video room (using scheduledDateTime directly)
  const getTimeUntilVideoRoom = (scheduledDateTime) => {
    if (!scheduledDateTime) return null;
    
    try {
      const roomDateTime = new Date(scheduledDateTime);
      const now = new Date();
      
      if (isNaN(roomDateTime.getTime())) {
        return null;
      }
      
      const diffMs = roomDateTime - now;
      
      if (diffMs < 0) {
        return { text: 'Past', type: 'past' };
      }
      
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffDays > 0) {
        return { text: `${diffDays}d`, type: diffDays <= 1 ? 'urgent' : 'upcoming' };
      } else if (diffHours > 0) {
        return { text: `${diffHours}h`, type: diffHours <= 2 ? 'urgent' : 'upcoming' };
      } else {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        return { text: `${diffMinutes}m`, type: 'urgent' };
      }
    } catch (error) {
      console.error('Error calculating time until video room:', error);
      return null;
    }
  };

  // Get time indicator color
  const getTimeIndicatorColor = (type) => {
    switch (type) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'upcoming': return 'bg-yellow-100 text-yellow-800';
      case 'past': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get mode badge color
  const getModeColor = (mode) => {
    switch (mode) {
      case 'GOOGLE': return 'bg-blue-100 text-blue-800';
      case 'CALENDLY': return 'bg-purple-100 text-purple-800';
      case 'EMAIL': return 'bg-green-100 text-green-800';
      case 'OTHER': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Render sort icon
  // Note: Sort icons removed since we use backend sorting

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Interview Schedule Manager</h1>
          <p className="text-gray-600">Manage and view all scheduled interviews and video rooms</p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('scheduled')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'scheduled'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-2" />
                  Scheduled Interviews
                </div>
              </button>
              <button
                onClick={() => setActiveTab('video-rooms')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'video-rooms'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <Video className="h-4 w-4 mr-2" />
                  Video Rooms
                </div>
              </button>
            </nav>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Candidate Name Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Candidate Name
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.candidateName}
                  onChange={(e) => handleFilterChange('candidateName', e.target.value)}
                  placeholder="Search by name..."
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Job Title Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Title
              </label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.jobTitle}
                  onChange={(e) => handleFilterChange('jobTitle', e.target.value)}
                  placeholder="Search by job title..."
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Date From Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date From
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Date To Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date To
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* Clear Filters Button */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-4 flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-600">
              Showing {sortedData.length} of {pagination.total} {activeTab === 'scheduled' ? 'interviews' : 'video rooms'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {activeTab === 'scheduled'
                ? 'Sorted by upcoming interviews first, then past interviews (backend sorted)'
                : 'Sorted by upcoming video rooms first, then past video rooms (backend sorted)'
              }
            </p>
            {(() => {
              const currentData = activeTab === 'scheduled' ? interviews : videoRooms;
              const upcomingCount = currentData.filter(item => {
                const timeUntil = activeTab === 'scheduled' 
                  ? getTimeUntilInterview(item.date, item.time)
                  : getTimeUntilVideoRoom(item.scheduledDateTime);
                return timeUntil && timeUntil.type !== 'past';
              }).length;
              const pastCount = currentData.length - upcomingCount;
              
              return (
                <div className="flex gap-4 mt-2">
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                    {upcomingCount} Upcoming
                  </span>
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-full">
                    {pastCount} Past
                  </span>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading interviews...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="py-3 px-4 text-left font-bold text-gray-600 text-sm">
                      <div className="flex items-center">
                        <User className="h-4 w-4 text-blue-600 mr-2" />
                        Candidate
                      </div>
                    </TableHead>
                    <TableHead className="font-bold text-gray-600 text-sm">
                      <div className="flex items-center">
                        <Briefcase className="h-4 w-4 text-blue-600 mr-2" />
                        Job Title
                      </div>
                    </TableHead>
                    <TableHead className="font-bold text-gray-600 text-sm">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-blue-600 mr-2" />
                        Date
                      </div>
                    </TableHead>
                    <TableHead className="font-bold text-gray-600 text-sm">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 text-blue-600 mr-2" />
                        Time
                      </div>
                    </TableHead>
                    <TableHead className="font-bold text-gray-600 text-sm">Duration</TableHead>
                    <TableHead className="font-bold text-gray-600 text-sm">Time Until</TableHead>
                    <TableHead className="font-bold text-gray-600 text-sm">Mode</TableHead>
                    <TableHead className="font-bold text-gray-600 text-sm">Type</TableHead>
                    <TableHead className="font-bold text-gray-600 text-sm">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedData.map((item, index) => {
                    // Handle both interview and video room data
                    const isInterview = activeTab === 'scheduled';
                    const timeUntil = isInterview 
                      ? getTimeUntilInterview(item.date, item.time) 
                      : getTimeUntilVideoRoom(item.scheduledDateTime);
                    const isUpcoming = timeUntil && timeUntil.type !== 'past';
                    const isFirstUpcoming = isUpcoming && index === 0;
                    const isFirstPast = !isUpcoming && index > 0 && 
                      sortedData.slice(0, index).every(prev => {
                        const prevTimeUntil = isInterview 
                          ? getTimeUntilInterview(prev.date, prev.time)
                          : getTimeUntilVideoRoom(prev.scheduledDateTime);
                        return prevTimeUntil && prevTimeUntil.type !== 'past';
                      });

                    return (
                      <React.Fragment key={item.id}>
                        {/* Section separator for upcoming interviews */}
                        {isFirstUpcoming && (
                          <TableRow className="bg-green-100">
                            <TableCell colSpan="9" className="text-center py-3">
                              <div className="flex items-center justify-center">
                                <div className="flex-1 border-t border-green-300"></div>
                                <span className="px-4 text-sm font-medium text-green-700 bg-green-100">
                                  {activeTab === 'scheduled' ? 'Upcoming Interviews' : 'Upcoming Video Rooms'}
                                </span>
                                <div className="flex-1 border-t border-green-300"></div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                        
                        {/* Section separator for past interviews */}
                        {isFirstPast && (
                          <TableRow className="bg-gray-100">
                            <TableCell colSpan="9" className="text-center py-3">
                              <div className="flex items-center justify-center">
                                <div className="flex-1 border-t border-gray-300"></div>
                                <span className="px-4 text-sm font-medium text-gray-600 bg-gray-100">
                                  {activeTab === 'scheduled' ? 'Past Interviews' : 'Past Video Rooms'}
                                </span>
                                <div className="flex-1 border-t border-gray-300"></div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                        
                        <TableRow
                          className={`border-b border-gray-200 hover:bg-gray-50 ${
                            index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                          } ${isUpcoming ? 'bg-green-50' : ''}`}
                        >
                      <TableCell className="font-medium">
                        <div>
                          <div className="font-semibold text-gray-900">
                            {isInterview ? item.candidateName : (item.candidateName || 'N/A')}
                          </div>
                          <div className="text-sm text-gray-500">
                            {isInterview ? item.candidateEmail : (item.candidateEmail || 'N/A')}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-900">
                        {isInterview && item.jobId ? (
                          <button
                            onClick={() => handleViewJob(item)}
                            className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
                          >
                            {item.jobTitle}
                          </button>
                        ) : (
                          <span className="text-gray-500">
                            {isInterview ? item.jobTitle : (item.jobTitle || 'N/A')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-900">
                        {isInterview ? formatDate(item.date) : (item.scheduledDateTime ? formatDate(new Date(item.scheduledDateTime).toLocaleDateString('en-GB')) : 'N/A')}
                      </TableCell>
                      <TableCell className="text-gray-900">
                        {isInterview ? formatTime(item.time) : (item.scheduledDateTime ? new Date(item.scheduledDateTime).toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'}) : 'N/A')}
                      </TableCell>
                      <TableCell className="text-gray-900">
                        {isInterview ? `${item.duration} min` : (item.duration ? `${item.duration} min` : 'N/A')}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const timeUntil = isInterview 
                            ? getTimeUntilInterview(item.date, item.time) 
                            : getTimeUntilVideoRoom(item.scheduledDateTime);
                          return timeUntil ? (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTimeIndicatorColor(timeUntil.type)}`}>
                              {timeUntil.text}
                            </span>
                          ) : (
                            <span className="text-gray-500 text-xs">N/A</span>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getModeColor(isInterview ? item.mode : 'VIDEO')}`}>
                          {isInterview ? item.mode : 'VIDEO'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getModeColor(isInterview ? item.on : 'VIDEO')}`}>
                          {isInterview ? item.on : 'VIDEO'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 flex-wrap">
                          {isInterview && item.jobId && (
                            <button
                              onClick={() => handleViewJob(item)}
                              className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <Briefcase className="h-3 w-3 mr-1" />
                              View Job
                            </button>
                          )}
                          {((isInterview && item.hasVideoRoom && item.interviewerJoinUrl) || (!isInterview && item.interviewerJoinUrl)) && (
                            <button
                              onClick={() => handleJoinCall(item)}
                              title="Join the video interview (opens in new tab)"
                              className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                              <Video className="h-3 w-3 mr-1" />
                              Join Interview
                            </button>
                          )}
                          <button
                            onClick={() => setDeleteModal({ isOpen: true, interview: item })}
                            className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                    </React.Fragment>
                  );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Empty State */}
          {!loading && getCurrentData().length === 0 && (
            <div className="p-8 text-center">
              {activeTab === 'scheduled' ? (
                <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              ) : (
                <Video className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              )}
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {activeTab === 'scheduled' ? 'No interviews found' : 'No video rooms found'}
              </h3>
              <p className="text-gray-600">Try adjusting your filters or check back later.</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.total > pagination.limit && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} results
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(Math.max(0, pagination.offset - pagination.limit))}
                disabled={pagination.offset === 0}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.offset + pagination.limit)}
                disabled={!pagination.hasMore}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">
                Delete {activeTab === 'video-rooms' ? 'Video Room' : 'Interview'}
              </h3>
            </div>
            <p className="text-gray-600 mb-6">
              {activeTab === 'video-rooms' 
                ? 'Are you sure you want to delete this video room? This action cannot be undone.'
                : 'Are you sure you want to delete this interview? This will also delete any associated video room.'
              }
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteModal({ isOpen: false, interview: null })}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteInterview}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewScheduleManager;
